import http from '@/http';
import requestUrlFunction from '@/utils/requestUrl';
import cancelRequest from '@/utils/cancelRequest';

export default {
  namespaced: true,
  state: {
    cancelTokens: [],
  },
  getters: {
    cancelTokens(state) {
      return state.cancelTokens;
    },
  },
  mutations: {
    ADD_CANCEL_TOKEN(state, token) {
      state.cancelTokens.push(token);
    },
    CLEAR_CANCEL_TOKENS(state) {
      state.cancelTokens = [];
    },
  },
  actions: {
    cancel_pending_requests(context) {
      context.state.cancelTokens.forEach((request) => {
        if (request.cancel) {
          request.cancel();
        }
      });

      context.commit('CLEAR_CANCEL_TOKENS');
    },
    async searchRequests(context, params) {
      const response = await http.get(`requests/${requestUrlFunction(params)}`, cancelRequest(null, params));

      return response;
    },
    async getRequest(context, id) {
      const response = await http.get(`requests/${id}`);

      return response;
    },
    async getRequestActivities(context, params) {
      const response = await http.get(`requests/${params.id}/activities${requestUrlFunction(params)}`, cancelRequest(null, params));

      return response;
    },
    async getStatuses() {
      const response = await http.get('requests/enumeration/request-statuses');

      return response;
    },
    async getTypes() {
      const response = await http.get('requests/enumeration/request-types');

      return response;
    },
    async getIndividualReadingTypes() {
      const response = await http.get('requests/enumeration/individual-reading-types');

      return response;
    },
    async getAgreementAttachmentsHistorical(context, params) {
      const response = await http.get(`requests/agreement-attachments/${requestUrlFunction(params)}`, cancelRequest(null, params));

      return response;
    },
    async sendIndividualReadingRequest(context, request) {
      const response = await http.put(`requests/installation/${request.ean}/reading`, request.reportReading);

      return response;
    },
    async sendIndividualElectricityInterruptionRequest(context, request) {
      const response = await http.put(`requests/installation/${request.ean}/interruption-electricity`, request.interruptionOrRenewal);

      return response;
    },
    async sendIndividualRenewalElectricityRequest(context, request) {
      const response = await http.put(`requests/installation/${request.ean}/renewal-electricity`, request.interruptionOrRenewal);

      return response;
    },
    async sendIndividualBookedCapacityRequest(context, request) {
      const response = await http.put(`requests/installation/${request.ean}/booked-capacity`, request.individualCapacityChangeMonthly);

      return response;
    },
    async sendIndividualReservedPowerChange(context, request) {
      const formData = new FormData();
      formData.append('reservedPower', request.reservedPower);
      formData.append('requiredChangeDate', request.requiredChangeDate);
      formData.append('file', request.file);
      const response = await http.put(`requests/installation/${request.ean}/vn-agreement-update/reserved-power-change`, formData);

      return response;
    },
    async sendIndividualConnectionAgreementHv(context, request) {
      const formData = new FormData();
      formData.append('data', new Blob([
        JSON.stringify(request.individualConnectionAgreementHv),
      ], {
        type: 'application/json',
      }));
      formData.append('attachment', request.attachment);
      formData.append('contactSubstitutePowerOfAttorney', request.contactSubstitutePowerOfAttorney);
      const response = await http.put(`requests/installation/${request.ean}/connection-agreement-request-hv`, formData);

      return response;
    },
    async sendIndividualConnectionAgreementLv(context, request) {
      const formData = new FormData();
      formData.append('data', new Blob([
        JSON.stringify(request.individualConnectionAgreementLv),
      ], {
        type: 'application/json',
      }));
      formData.append('attachment', request.attachment);
      formData.append('powerOfAttorney', request.powerOfAttorney);
      const response = await http.put(`requests/installation/${request.ean}/connection-agreement-request-lv`, formData);

      return response;
    },
    async sendIndividualControlStagesRequest(context, request) {
      const response = await http.put(`requests/installation/${request.ean}/vn-agreement-update/control-stages-change`, request.individualControlStagesChange);

      return response;
    },
    async sendIndividualReservedPowerRequest(context, request) {
      const response = await http.put(`requests/installation/${request.ean}/vn-agreement-update/reserved-power-change`, request.individualReservedPowerChange);

      return response;
    },
    async createNewBulkReadingRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/readings', formDataFile);

      return response;
    },
    async createNewBulkBookedCapacityRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/booked-capacities', formDataFile);

      return response;
    },
    async createNewBulkElectricityInterruptionRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/interruptions-electricity', formDataFile);

      return response;
    },
    async createNewBulkRenewalElectricityRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/renewals-electricity', formDataFile);

      return response;
    },
    async createNewBulkControlStagesRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/vn-agreement-update/control-stages-changes', formDataFile);

      return response;
    },
    async createNewBulkOtherRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/vn-agreement-update/other-changes', formDataFile);

      return response;
    },
    async createNewBulkLVAgreementRequest(context, file) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      const response = await http.put('requests/installation/lv-agreement-update', formDataFile);

      return response;
    },
    async sendLVAgreementToSAP(context, request) {
      const response = await http.put(`requests/${request.id}/lv-agreement-send-to-sap`, request);

      return response;
    },
    async sendLVAgreementSubrequestUpdate(context, request) {
      const formData = new FormData();
      formData.append('updateDto', new Blob([
        JSON.stringify(request.updateDto),
      ], {
        type: 'application/json',
      }));
      formData.append('attachment', request.attachment1);
      formData.append('attachment', request.attachment2);
      formData.append('attachment', request.attachment3);
      formData.append('attachment', request.attachment4);
      const response = await http.put(`requests/${request.id}/lv-agreement-update`, formData);

      return response;
    },
    async getRequestedLengths() {
      const response = await http.get('requests/enumeration/requested-lengths');

      return response;
    },
    async sendTrialOperationRequest(context, request) {
      const formData = new FormData();
      Object.entries(request.trialOperation).forEach(([key, value]) => {
        if (value) {
          formData.append(
            key,
            value,
          );
        }
      });
      if (request.file) {
        formData.append('file', request.file);
      }
      const response = await http.put(`requests/installation/${request.ean}/trial-operation`, formData);

      return response;
    },
    async sendBillingExceptionRequest(context, request) {
      const formData = new FormData();
      Object.entries(request.billingException).forEach(([key, value]) => {
        if (value) {
          formData.append(
            key,
            value,
          );
        }
      });
      if (request.file) {
        formData.append('file', request.file);
      }
      const response = await http.put(`requests/installation/${request.ean}/billing-exception`, formData);

      return response;
    },
  },
};
